import { useState } from 'react';
import { WatchQueryOptions } from '@apollo/client';
// Types
import {
  GetStream,
  GetStreamVariables,
  GetStream_stream,
} from 'api/streams/types/GetStream';
// Helpers
import { formatTicketEventTagDate } from 'helpers/ticketSales';
// Hooks
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
import { useOrderStreamInfo } from './hooks/useOrderStreamInfo';
// Components
import OrderStreamPayments from 'components/OrderStream/OrderStreamPayments/OrderStreamPayments';
import StreamDetailsModal from './StreamDetailsModal/StreamDetailsModal';
import TalentBlock from 'components/common3/TalentBlock/TalentBlock';
import StreamImage from './components/StreamImage/StreamImage';
import StreamDescription from './components/StreamDescription/StreamDescription';
import TicketPriceBox from 'components/TicketEvents/TicketPriceBox/TicketPriceBox';
import AboutEventModal from 'components/TicketEvents/AboutEventModal/AboutEventModal';
// UI
import Text from 'ui3/Text/Text';
// Styles
import styles from './OrderStreamInfo.module.scss';

type OrderStreamInfoProps = {
  stream: GetStream_stream | undefined;
  updateStreamQuery: <TVars = GetStreamVariables>(
    mapFn: (
      previousQueryResult: GetStream,
      options: Pick<WatchQueryOptions<TVars, GetStream>, 'variables'>
    ) => GetStream
  ) => void;
  isFeaturedStream?: boolean;
};

const OrderStreamInfo = ({
  stream,
  updateStreamQuery,
  isFeaturedStream = false,
}: OrderStreamInfoProps) => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const streamInfo = useOrderStreamInfo(stream);
  const ticketingLaunched = useTicketingLaunched();

  if (!stream || !streamInfo) {
    return null;
  }

  const {
    name,
    imageURL,
    streamStatus,
    store,
    hashtags,
    description,
    mentions,
    views,
    isInterview,
  } = stream;
  // An event's date reads in its venue's time zone, as its price box and event page do.
  const ticketEvent = ticketingLaunched ? stream.ticketEvent : null;

  const {
    isUserStreamCreator,
    streamerName,
    isStreamCanceled,
    isStreamEnded,
    socialShareButtonText,
    streamDateTimeText,
    streamDateShort,
    videoDetails,
    profilePath,
    avatarURL,
    descriptionPreview,
    mobileDescriptionPreview,
  } = streamInfo;

  const showOfficialPpvTag = isFeaturedStream && !stream.isFree;
  // Disclaimer only applies to PPV streams, not free streams or interviews
  const showDisclaimer = !stream.isFree && !isInterview;

  return (
    <>
      <div className={styles.rootOuter}>
        <div className={styles.root}>
          {/* Mobile Top Wrapper - Talent + Image */}
          <div className={styles.mobileTopWrapper}>
            <TalentBlock
              store={store}
              talentName={streamerName}
              avatarURL={avatarURL}
              profilePath={profilePath}
              showFollowButton={!isUserStreamCreator}
              className={styles.talentSectionMobile}
            />
            <StreamImage
              imageURL={imageURL}
              name={name}
              wrapperClassName={styles.imageContainerMobile}
            />
          </div>

          <div className={styles.infoWrapper}>
            {/* Tags Row: Official PPV (if its a featured stream) + Date */}
            <div className={styles.tagsRow}>
              {showOfficialPpvTag && (
                <div className={styles.officialPpvTag}>
                  <Text variant="smallLabelBold" color="background-dim">
                    Official PPV
                  </Text>
                </div>
              )}
              <div className={styles.dateTag}>
                <Text variant="smallLabelBold" color="background-dim">
                  {ticketEvent
                    ? formatTicketEventTagDate(
                        ticketEvent.startsAt,
                        ticketEvent.timezone
                      )
                    : streamDateShort}
                </Text>
              </div>
            </div>

            <div className={styles.infoBlock}>
              <Text as="h1" variant="h4" className={styles.title}>
                {name || ''}
              </Text>

              {/* Mobile Payments */}
              {ticketEvent && (
                <div className={styles.paymentsMobile}>
                  <TicketPriceBox event={ticketEvent} />
                </div>
              )}
              {!isStreamCanceled && (
                <div className={styles.paymentsMobile}>
                  <OrderStreamPayments
                    stream={stream}
                    updateStreamQuery={updateStreamQuery}
                    socialShareButtonText={socialShareButtonText}
                    isInterview={isInterview}
                  />
                </div>
              )}

              {/* Desktop Talent Section */}
              <TalentBlock
                store={store}
                talentName={streamerName}
                avatarURL={avatarURL}
                profilePath={profilePath}
                showFollowButton={!isUserStreamCreator}
              />

              {/* Description Section */}
              <StreamDescription
                description={description}
                descriptionPreview={descriptionPreview}
                mobileDescriptionPreview={mobileDescriptionPreview}
                streamDateTimeText={streamDateTimeText}
                videoDetails={videoDetails}
                hashtags={hashtags}
                views={views}
                isStreamEnded={isStreamEnded}
                onOpenModal={() => setIsDetailsModalOpen(true)}
                showDisclaimer={showDisclaimer}
              />

              {/* Desktop Payments */}
              {ticketEvent && (
                <div className={styles.paymentsDesktop}>
                  <TicketPriceBox event={ticketEvent} />
                </div>
              )}
              {!isStreamCanceled && (
                <div className={styles.paymentsDesktop}>
                  <OrderStreamPayments
                    stream={stream}
                    updateStreamQuery={updateStreamQuery}
                    socialShareButtonText={socialShareButtonText}
                    isInterview={isInterview}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Desktop Image Container */}
          <StreamImage
            imageURL={imageURL}
            name={name}
            wrapperClassName={styles.imageContainer}
          />
        </div>
      </div>

      {/* Desktop Modal */}
      {ticketEvent ? (
        <AboutEventModal
          event={ticketEvent}
          open={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          isHost={isUserStreamCreator}
          showDisclaimer={showDisclaimer}
        />
      ) : (
        <StreamDetailsModal
          open={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          description={description}
          dateTimeText={streamDateTimeText}
          mentions={mentions || []}
          hashtags={hashtags || []}
          views={views?.totalViewCount ?? null}
          streamStatus={streamStatus}
          showDisclaimer={showDisclaimer}
        />
      )}
    </>
  );
};

export default OrderStreamInfo;
